// src/markets/harvestPace.js — NASS harvest pace, this week against the 5-year average.
//
// One rule, two callers: src/markets/HarvestPaceCard.jsx renders it and
// tools/check-nass-harvest-pace.js drives it. Import-free on purpose, so the checker
// loads the SHIPPED source.
//
// -- WHAT IS SOURCED, AND HOW IT WAS CHECKED -----------------------------------
//
// * The series. NASS Crop Progress publishes, for NEW YORK, three separate weekly
//   % HARVESTED series: corn for GRAIN, corn for SILAGE, and SOYBEANS (probed
//   against Quick Stats 2026-10-20). Grain and silage are different harvests weeks
//   apart and are NEVER combined here — each is its own stat_type in nass_data.
//
// * The week. nass_data keeps NASS's week number ("WEEK #42") and not the
//   week-ending date. NASS's week number is the ISO-8601 week of the Sunday the
//   week ends on; nassWeekEnding() inverts that and reproduces NASS's own
//   week_ending field on 962 of 962 harvest rows (2015-2026, all three series,
//   actual + 5-year-average + previous-year variants).
//
// * The 5-year average. NASS publishes its own "5 YEAR AVG" series but not for
//   every week (for 2026 NY corn grain it has published none at all), so it is
//   DERIVED here — the way NASS derives it, measured rather than assumed: each of
//   the five prior years is read on the SAME CALENDAR DATE as this week's
//   week-ending, linearly interpolated between that year's published weeks (0
//   before its first report, its last report held after its final one), and the
//   five are averaged. Replayed against every week NASS DID publish a 5-year
//   average for (2020-2026, all three series) this reproduces NASS's rounded
//   figure on 142 of 143 weeks, and the 143rd is one point off. Aligning by WEEK
//   NUMBER instead — the obvious method — matches on 12 of 142, because a week
//   number lands up to six days apart from one year to the next.
//
// * A gap. NASS published no NY Crop Progress between 2025-09-28 and 2025-11-16.
//   NASS's own 2026 silage average bridges that gap (its 66 for 2026-10-11 matches
//   the bridged 65.4, not the 71.0 you get by dropping 2025), so the average does
//   the same and SAYS it did. "Last year" does not: NASS publishes no previous-year
//   figure for those weeks, so a last-year reading that would need the bridge is a
//   named absence, never a number.
//
// A missing week is never a zero. If NASS has not published the week this card
// expects, it says so and shows the last week it has.

export const HARVEST_SERIES = [
  { key: "grain",  crop: "CORN",     stat: "PCT_HARVESTED_GRAIN",  label: "Corn for grain" },
  { key: "silage", crop: "CORN",     stat: "PCT_HARVESTED_SILAGE", label: "Corn for silage" },
  { key: "soy",    crop: "SOYBEANS", stat: "PCT_HARVESTED",        label: "Soybeans" },
];

// NASS reports weekly. Two published weeks further apart than this means a week
// (or more) with no report in between.
export const WEEKLY_GAP_DAYS = 7;

const DAY = 86400000;
const toMs = (iso) => Date.parse(iso + "T00:00:00Z");
const toIso = (ms) => new Date(ms).toISOString().slice(0, 10);

// Sunday that ends ISO week `week` of `year`.
export function nassWeekEnding(year, week) {
  const y = Number(year), w = Number(week);
  if (!Number.isInteger(y) || !Number.isInteger(w) || w < 1 || w > 53) return null;
  const jan4 = Date.UTC(y, 0, 4);
  const dow = (new Date(jan4).getUTCDay() + 6) % 7;          // Monday = 0
  const monday1 = jan4 - dow * DAY;
  return toIso(monday1 + ((w - 1) * 7 + 6) * DAY);
}

// Rows for ONE series. A row whose value is not a number is dropped, never zeroed.
export function seriesFor(rows, def) {
  return (rows || [])
    .filter((r) => r && r.crop === def.crop && r.stat_type === def.stat && r.week != null && r.value !== null && r.value !== "" && Number.isFinite(Number(r.value)))
    .map((r) => ({ year: Number(r.year), week: Number(r.week), weekEnding: nassWeekEnding(r.year, r.week), value: Number(r.value) }))
    .filter((r) => r.weekEnding)
    .sort((a, b) => (a.weekEnding < b.weekEnding ? -1 : a.weekEnding > b.weekEnding ? 1 : 0));
}

// One year's reading on a calendar date (YYYY-MM-DD within that year).
//   how: published | interpolated | bridged | not_started | finished | none
export function valueOnDate(series, year, iso) {
  const s = series.filter((r) => r.year === year);
  if (!s.length) return { value: null, how: "none" };
  const t = toMs(iso);
  const first = s[0], last = s[s.length - 1];
  if (t < toMs(first.weekEnding)) return { value: 0, how: "not_started" };
  if (t > toMs(last.weekEnding)) return { value: last.value, how: "finished" };
  for (let i = 0; i < s.length; i++) {
    const b = s[i];
    const tb = toMs(b.weekEnding);
    if (tb === t) return { value: b.value, how: "published" };
    if (tb > t) {
      const a = s[i - 1];
      const ta = toMs(a.weekEnding);
      const gapDays = Math.round((tb - ta) / DAY);
      const value = a.value + (b.value - a.value) * (t - ta) / (tb - ta);
      return { value, how: gapDays > WEEKLY_GAP_DAYS ? "bridged" : "interpolated", gapDays, from: a.weekEnding, to: b.weekEnding };
    }
  }
  return { value: null, how: "none" };
}

const sameDateIn = (iso, year) => `${year}${iso.slice(4)}`;

// The five prior years on the same calendar date. All five must exist.
export function fiveYearAverage(series, year, iso) {
  const years = [];
  for (let y = year - 5; y < year; y++) {
    const v = valueOnDate(series, y, sameDateIn(iso, y));
    years.push({ year: y, ...v });
  }
  const missing = years.filter((y) => y.how === "none");
  if (missing.length) {
    return { value: null, rounded: null, years, reason: `no NASS harvest reports for ${missing.map((m) => m.year).join(", ")}` };
  }
  const value = years.reduce((s, y) => s + y.value, 0) / years.length;
  return { value, rounded: Math.round(value), years, bridged: years.filter((y) => y.how === "bridged") };
}

// Last year on the same date — but never across a gap NASS did not report through.
export function lastYearOnDate(series, year, iso) {
  const v = valueOnDate(series, year - 1, sameDateIn(iso, year - 1));
  if (v.how === "none") return { value: null, absent: true, reason: `no NASS harvest reports for ${year - 1}` };
  if (v.how === "bridged") return { value: null, absent: true, reason: `NASS published no report that week in ${year - 1}` };
  return { value: Math.round(v.value), absent: false, how: v.how };
}

// The most recent week-ending this card can expect NASS to have published by
// `todayIso`: the latest Sunday at least two days back. Crop Progress releases on
// Monday afternoon (Tuesday in a holiday week), so from Tuesday on last Sunday's
// report is due; before then the one before it is.
export function expectedWeekEnding(todayIso) {
  let t = toMs(todayIso) - 2 * DAY;
  while (new Date(t).getUTCDay() !== 0) t -= DAY;
  return toIso(t);
}

export function paceWord(diff) {
  if (diff == null) return null;
  return diff > 0 ? "ahead" : diff < 0 ? "behind" : "even";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fmtWeekEnding = (iso) => (iso ? `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}, ${iso.slice(0, 4)}` : "");

// The whole reading for one series.
//   state: no_history | not_started | in_season | complete
export function harvestPace(rows, def, todayIso) {
  const series = seriesFor(rows, def);
  const year = Number(String(todayIso).slice(0, 4));
  const base = { key: def.key, label: def.label, year };
  if (!series.length) {
    return { ...base, state: "no_history", sentence: `${def.label}: NASS harvest progress is not loaded.` };
  }
  const cur = series.filter((r) => r.year === year);
  if (!cur.length) {
    return { ...base, state: "not_started", sentence: `${def.label}: NASS has not published ${year} harvest progress yet.` };
  }
  const latest = cur[cur.length - 1];
  const avg = fiveYearAverage(series, year, latest.weekEnding);
  const lastYear = lastYearOnDate(series, year, latest.weekEnding);
  const diff = avg.rounded == null ? null : latest.value - avg.rounded;
  const pace = paceWord(diff);
  const expected = expectedWeekEnding(todayIso);
  const complete = latest.value >= 100;
  // Weeks NASS has not published since the latest one — named, never zeroed.
  const missingWeeks = [];
  if (!complete) {
    for (let t = toMs(latest.weekEnding) + 7 * DAY; t <= toMs(expected); t += 7 * DAY) missingWeeks.push(toIso(t));
  }

  let head;
  if (diff == null) head = `${def.label}: ${latest.value}% harvested — 5-year average not available (${avg.reason}).`;
  else if (pace === "even") head = `${def.label}: ${latest.value}% harvested — even with the 5-year average (${avg.rounded}%).`;
  else head = `${def.label}: ${latest.value}% harvested — ${Math.abs(diff)} point${Math.abs(diff) === 1 ? "" : "s"} ${pace === "ahead" ? "ahead of" : "behind"} the 5-year average (${avg.rounded}%).`;
  const ly = lastYear.absent ? `Last year: ${lastYear.reason}.` : `Last year ${lastYear.value}%.`;
  const sentence = `${head} ${ly} Week ending ${fmtWeekEnding(latest.weekEnding)}.`;

  return {
    ...base,
    state: complete ? "complete" : "in_season",
    current: latest,
    average: avg,
    lastYear,
    diff,
    pace,
    expected,
    missingWeeks,
    sentence,
  };
}

export function harvestPaceAll(rows, todayIso) {
  return HARVEST_SERIES.map((def) => harvestPace(rows, def, todayIso));
}
